import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";

const RESEND_DEFAULT = 30;
const CHANNELS = [
  { key: "sms", label: "SMS" },
  { key: "whatsapp", label: "WhatsApp" },
];

export default function PhoneOtpModal({ open, onOpenChange }) {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("sms"); // Default: SMS
  const [requiresName, setRequiresName] = useState(false);
  const [agreed, setAgreed] = useState(true); // Default: checked
  const [loading, setLoading] = useState(false);
  const [lastChannel, setLastChannel] = useState("sms");
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  const { login } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  // Build per-tenant legal URLs — these point at the same pages
  // exposed under /legal/{kind} on the storefront.
  const slug = branding?.tenant_slug;
  const legalBase = slug && slug !== "default" ? `/t/${slug}/legal` : "/legal";

  const reset = () => {
    setStep("phone"); setPhone(""); setCode(""); setName(""); setRequiresName(false);
    setChannel("sms"); setLastChannel("sms"); setResendIn(0); setAgreed(true);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const startCountdown = (seconds) => {
    setResendIn(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current); timerRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const requestOtp = async (overrideChannel) => {
    if (!phone || phone.length < 10) { toast.error("Enter a valid phone number"); return; }
    if (!agreed) { toast.error("Please accept the Terms and Privacy Policy"); return; }
    const ch = overrideChannel || channel;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone, channel: ch });
      setLastChannel(data.channel || ch);
      setRequiresName(!!data.requires_name);
      const successMsg = data.fallback
        ? "WhatsApp delivery failed — OTP sent via SMS instead."
        : data.channel === "whatsapp"
          ? "OTP sent via WhatsApp."
          : "OTP sent via SMS.";
      toast.success(successMsg);
      setStep("otp");
      startCountdown(data.resend_in || RESEND_DEFAULT);
    } catch (e) {
      const status = e?.response?.status;
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
      if (status === 429) {
        const retryAfter = parseInt(e?.response?.headers?.["retry-after"] || "0", 10);
        if (retryAfter > 0) startCountdown(retryAfter);
      }
    } finally { setLoading(false); }
  };

  const verify = async () => {
    if (!code || code.length < 4) { toast.error("Enter the OTP"); return; }
    if (requiresName && !name.trim()) { toast.error("Please tell us your name"); return; }
    setLoading(true);
    try {
      const u = await login(phone, code, name.trim() || undefined);
      toast.success(`Welcome, ${u.name || u.phone}`);
      onOpenChange(false);
      reset();
      if (u.role === "super_admin") navigate("/superadmin");
      else if (u.role === "admin") navigate("/admin");
      else if (u.role === "seller") navigate("/seller");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  const canResend = resendIn === 0 && !loading;
  const canSend = phone.length >= 10 && agreed && !loading;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-md rounded-none border-[hsl(var(--kb-border))]" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight">
            {step === "phone" ? "Sign in" : "Enter OTP"}
          </DialogTitle>
          <DialogDescription className="overline">
            {step === "phone"
              ? "Choose how you'd like to receive your OTP"
              : `Sent via ${lastChannel === "whatsapp" ? "WhatsApp" : "SMS"} to +91 ${phone}`}
          </DialogDescription>
        </DialogHeader>

        {step === "phone" ? (
          <div className="space-y-5 pt-4">
            <input
              data-testid="phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              placeholder="Phone number"
              className="kb-input"
              maxLength={10}
              autoFocus
            />

            {/* Channel radio — defaults to SMS */}
            <div role="radiogroup" aria-label="Delivery channel" className="space-y-2" data-testid="channel-radio-group">
              {CHANNELS.map((c) => (
                <label
                  key={c.key}
                  className={[
                    "flex items-center gap-3 px-4 py-3 border cursor-pointer transition-colors",
                    channel === c.key
                      ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-surface-alt))]"
                      : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/40",
                  ].join(" ")}
                >
                  <input
                    type="radio"
                    name="otp-channel"
                    value={c.key}
                    checked={channel === c.key}
                    onChange={() => setChannel(c.key)}
                    className="accent-[hsl(var(--kb-primary))]"
                    data-testid={`channel-radio-${c.key}`}
                  />
                  <span className="text-sm uppercase tracking-wider">{c.label}</span>
                </label>
              ))}
            </div>

            {/* T&C checkbox — defaults to checked */}
            <label className="flex items-start gap-2 text-xs text-[hsl(var(--kb-muted))]" data-testid="terms-checkbox-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 accent-[hsl(var(--kb-primary))]"
                data-testid="terms-checkbox"
              />
              <span>
                I agree to the{" "}
                <a href={`${legalBase}/terms`} target="_blank" rel="noreferrer" className="underline">Terms &amp; Conditions</a>
                {" "}and{" "}
                <a href={`${legalBase}/privacy`} target="_blank" rel="noreferrer" className="underline">Privacy Policy</a>.
              </span>
            </label>

            <button
              data-testid="send-otp-btn"
              disabled={!canSend}
              onClick={() => requestOtp()}
              className="kb-btn-primary w-full disabled:opacity-50"
            >
              {loading ? "Sending…" : "Send OTP"}
            </button>
          </div>
        ) : (
          <div className="space-y-5 pt-4">
            {requiresName && (
              <input
                data-testid="name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="kb-input"
                autoFocus
              />
            )}
            <input
              data-testid="otp-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="6-digit OTP"
              maxLength={6}
              className="kb-input tracking-[0.5em] text-center text-2xl"
              autoFocus={!requiresName}
            />
            <button
              data-testid="verify-otp-btn"
              disabled={loading}
              onClick={verify}
              className="kb-btn-primary w-full"
            >
              {loading ? "Verifying…" : "Verify & Sign in"}
            </button>

            <div className="flex items-center justify-between text-xs">
              <button
                data-testid="change-phone-btn"
                className="text-[hsl(var(--kb-muted))] underline"
                onClick={() => { setStep("phone"); setCode(""); }}
              >
                Change phone
              </button>
              {resendIn > 0 ? (
                <span
                  data-testid="resend-countdown"
                  className="text-[hsl(var(--kb-muted))]"
                >
                  Resend in {resendIn}s
                </span>
              ) : (
                <div className="flex gap-3">
                  <button
                    data-testid="resend-whatsapp-btn"
                    disabled={!canResend}
                    onClick={() => requestOtp("whatsapp")}
                    className="text-[hsl(var(--kb-accent))] underline disabled:opacity-50"
                  >
                    Resend WhatsApp
                  </button>
                  <button
                    data-testid="resend-sms-btn"
                    disabled={!canResend}
                    onClick={() => requestOtp("sms")}
                    className="text-[hsl(var(--kb-accent))] underline disabled:opacity-50"
                  >
                    Resend SMS
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
